import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, ShoppingBag, Users, Eye, Calendar, ArrowUp, ArrowDown, BarChart3, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/constants';

// Ensure this environment variable is correctly set in your .env file

const Analytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user
  } = useSelector(state => state.auth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, period]);
  const loadDashboard = async () => {
    try {
      setLoading(true);

      // CRITICAL FIX: Explicitly check for the token before making the API call
      const token = localStorage.getItem('gigship_token');
      if (!token) {
        console.warn('Authentication token is missing. Preventing analytics API call.');
        setLoading(false);
        return;
      }
      const isGigger = user?.activeRole === 'gigger' || user?.roles?.includes('gigger');
      const endpoint = isGigger ? '/analytics/seller/dashboard' : '/analytics/buyer/dashboard';
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        // Token is guaranteed to be present here
        params: {
          period
        }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Analytics load error:', error.response?.data?.message || error.message);
      toast.error(error.response?.data?.message || 'Failed to load analytics');

      // Optional: Clear session if 401 is returned (e.g., expired token)
      if (error.response && error.response.status === 401) {
        // Handle unauthorized state (e.g., log out user)
      }
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">{t("dashboard.analytics.loading_analytics", {
            defaultValue: "Loading analytics..."
          })}</p>
        </div>
      </div>;
  }
  const isSeller = user?.activeRole === 'gigger' || user?.roles?.includes('gigger');
  return <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t("dashboard.analytics.analytics", {
              defaultValue: "Analytics"
            })}</h1>
            <p className="text-gray-600 mt-1">
              {isSeller ? 'Track your performance' : 'Your spending overview'}
            </p>
          </div>

          <select value={period} onChange={e => setPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="weekly">{t("dashboard.analytics.this_week", {
              defaultValue: "This Week"
            })}</option>
            <option value="monthly">{t("dashboard.analytics.this_month", {
              defaultValue: "This Month"
            })}</option>
            <option value="yearly">{t("dashboard.analytics.this_year", {
              defaultValue: "This Year"
            })}</option>
          </select>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {isSeller ? <>
              <StatCard title={t("dashboard.analytics.total_revenue", {
            defaultValue: "Total Revenue"
          })} value={`GH₵${stats?.revenue?.total?.toFixed(2) || '0.00'}`} change="+12%" icon={<DollarSign className="w-6 h-6" />} color="green" />
              <StatCard title={t("dashboard.analytics.monthly_revenue", {
            defaultValue: "Monthly Revenue"
          })} value={`GH₵${stats?.revenue?.monthly?.toFixed(2) || '0.00'}`} change="+8%" icon={<TrendingUp className="w-6 h-6" />} color="blue" />
              <StatCard title={t("dashboard.analytics.active_orders", {
            defaultValue: "Active Orders"
          })} value={stats?.orders?.active || 0} icon={<ShoppingBag className="w-6 h-6" />} color="orange" />
              <StatCard title={t("dashboard.analytics.completed_orders", {
            defaultValue: "Completed Orders"
          })} value={stats?.orders?.completed || 0} icon={<Users className="w-6 h-6" />} color="purple" />
            </> : <>
              <StatCard title={t("dashboard.analytics.total_spent", {
            defaultValue: "Total Spent"
          })} value={`GH₵${stats?.spending?.total?.toFixed(2) || '0.00'}`} icon={<DollarSign className="w-6 h-6" />} color="blue" />
              <StatCard title={t("dashboard.analytics.monthly_spending", {
            defaultValue: "Monthly Spending"
          })} value={`GH₵${stats?.spending?.monthly?.toFixed(2) || '0.00'}`} icon={<TrendingUp className="w-6 h-6" />} color="green" />
              <StatCard title={t("dashboard.analytics.active_orders", {
            defaultValue: "Active Orders"
          })} value={stats?.orders?.active || 0} icon={<ShoppingBag className="w-6 h-6" />} color="orange" />
              <StatCard title={t("dashboard.analytics.completed", {
            defaultValue: "Completed"
          })} value={stats?.orders?.completed || 0} icon={<Users className="w-6 h-6" />} color="purple" />
            </>}
        </div>

        {/* Additional Seller Stats */}
        {isSeller && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                {t("dashboard.analytics.gig_performance", {
              defaultValue: "Gig Performance"
            })}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.gigs", {
                  defaultValue: "Gigs"
                })}</span>
                  <span className="font-semibold">{stats?.gigs?.totalGigs || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.active_gigs", {
                  defaultValue: "Active Gigs"
                })}</span>
                  <span className="font-semibold text-green-600">{stats?.gigs?.activeGigs || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.total_views", {
                  defaultValue: "Total Views"
                })}</span>
                  <span className="font-semibold">{stats?.gigs?.totalViews || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.avg_rating", {
                  defaultValue: "Avg. Rating"
                })}</span>
                  <span className="font-semibold text-yellow-600">
                    {stats?.gigs?.averageRating?.toFixed(1) || '0.0'} ⭐
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                {t("dashboard.analytics.earnings_breakdown", {
              defaultValue: "Earnings Breakdown"
            })}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.available", {
                  defaultValue: "Available"
                })}</span>
                  <span className="font-semibold text-green-600">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{user?.wallet?.availableBalance?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.pending", {
                  defaultValue: "Pending"
                })}</span>
                  <span className="font-semibold text-orange-600">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.revenue?.pending?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.withdrawn", {
                  defaultValue: "Withdrawn"
                })}</span>
                  <span className="font-semibold text-gray-600">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.revenue?.withdrawn?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-900 font-medium">{t("dashboard.analytics.total_earned", {
                  defaultValue: "Total Earned"
                })}</span>
                  <span className="font-bold text-gray-900">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.revenue?.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-purple-600" />
                {t("dashboard.analytics.order_stats", {
              defaultValue: "Order Stats"
            })}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.this_week", {
                  defaultValue: "This Week"
                })}</span>
                  <span className="font-semibold">{stats?.orders?.weekly || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.this_month", {
                  defaultValue: "This Month"
                })}</span>
                  <span className="font-semibold">{stats?.orders?.monthly || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.cancelled", {
                  defaultValue: "Cancelled"
                })}</span>
                  <span className="font-semibold text-red-600">{stats?.orders?.cancelled || 0}</span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-900 font-medium">{t("dashboard.analytics.all_time", {
                  defaultValue: "All Time"
                })}</span>
                  <span className="font-bold text-gray-900">{stats?.orders?.total || 0}</span>
                </div>
              </div>
            </div>
          </div>}

        {/* Buyer Stats */}
        {!isSeller && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                {t("dashboard.analytics.spending_breakdown", {
              defaultValue: "Spending Breakdown"
            })}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.this_week", {
                  defaultValue: "This Week"
                })}</span>
                  <span className="font-semibold">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.spending?.weekly?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.this_month", {
                  defaultValue: "This Month"
                })}</span>
                  <span className="font-semibold">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.spending?.monthly?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.this_year", {
                  defaultValue: "This Year"
                })}</span>
                  <span className="font-semibold">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.spending?.yearly?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-900 font-medium">{t("dashboard.analytics.all_time", {
                  defaultValue: "All Time"
                })}</span>
                  <span className="font-bold text-gray-900">
                    {t("dashboard.analytics.gh", {
                  defaultValue: "GH\u20B5"
                })}{stats?.spending?.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-purple-600" />
                {t("dashboard.analytics.order_summary", {
              defaultValue: "Order Summary"
            })}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.in_progress", {
                  defaultValue: "In Progress"
                })}</span>
                  <span className="font-semibold text-blue-600">{stats?.orders?.inProgress || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.delivered", {
                  defaultValue: "Delivered"
                })}</span>
                  <span className="font-semibold text-purple-600">{stats?.orders?.delivered || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("dashboard.analytics.cancelled", {
                  defaultValue: "Cancelled"
                })}</span>
                  <span className="font-semibold text-red-600">{stats?.orders?.cancelled || 0}</span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-900 font-medium">{t('analytics.total_orders')}</span>
                  <span className="font-bold text-gray-900">{stats?.orders?.total || 0}</span>
                </div>
              </div>
            </div>
          </div>}

        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{t("dashboard.analytics.recent_orders", {
                defaultValue: "Recent Orders"
              })}</h3>
              <button onClick={() => navigate('/dashboard/orders')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                {t("dashboard.analytics.view_all", {
                defaultValue: "View All"
              })}
              </button>
            </div>
          </div>
          <div className="divide-y">
            {!stats?.recentOrders || stats.recentOrders.length === 0 ? <div className="p-8 text-center text-gray-500">
                <ShoppingBag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>{t("dashboard.analytics.no_orders_yet", {
                defaultValue: "No orders yet"
              })}</p>
                {!isSeller && <button onClick={() => navigate('/gigs')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {t("dashboard.analytics.browse_gigs", {
                defaultValue: "Browse Gigs"
              })}
                  </button>}
              </div> : stats.recentOrders.map(order => <div key={order.id || order._id} className="p-4 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/dashboard/orders/${order.id || order._id}`)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img src={isSeller ? order.buyer?.avatar : order.seller?.avatar} alt="User" className="w-10 h-10 rounded-full object-cover" onError={e => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/40?text=U';
                }} />
                      <div>
                        <p className="font-medium text-gray-900">
                          {order.gig?.title || order.gigTitle || 'Order'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {isSeller ? order.buyer?.username : order.seller?.username}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {t("dashboard.analytics.gh", {
                    defaultValue: "GH\u20B5"
                  })}{order.pricing?.totalAmount?.toFixed(2) || '0.00'}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${order.status === 'completed' ? 'bg-green-100 text-green-800' : order.status === 'active' || order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : order.status === 'delivered' ? 'bg-purple-100 text-purple-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>)}
          </div>
        </div>

        {/* Performance Tip */}
        {isSeller && <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t("dashboard.analytics.performance_tips", {
                defaultValue: "Performance Tips"
              })}</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>{t("dashboard.analytics.respond_to_messages_within_24_hours_to_i", {
                  defaultValue: "\u2022 Respond to messages within 24 hours to improve your rating"
                })}</li>
                  <li>{t("dashboard.analytics.complete_orders_on_time_to_maintain_a_hi", {
                  defaultValue: "\u2022 Complete orders on time to maintain a high completion rate"
                })}</li>
                  <li>{t("dashboard.analytics.update_your_gig_images_and_descriptions", {
                  defaultValue: "\u2022 Update your gig images and descriptions regularly"
                })}</li>
                  <li>{t("dashboard.analytics.encourage_buyers_to_leave_reviews_after", {
                  defaultValue: "\u2022 Encourage buyers to leave reviews after successful deliveries"
                })}</li>
                </ul>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};
const StatCard = ({
  title,
  value,
  icon,
  color,
  change
}) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600'
  };
  return <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        {change && <span className={`text-sm font-medium flex items-center ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
            {change.startsWith('+') ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
            {change}
          </span>}
      </div>
      <h3 className="text-sm text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>;
};
export default Analytics;